import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { pmSupabase as supabase } from '@/integrations/supabase/pmClient';

interface PMUser {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  programManagerId: string;
  role: string;
  permissions: string[];
  status: string;
  mfaEnforced: boolean;
  lastLoginAt: string | null;
}

interface PMAuthContextType {
  user: User | null;
  session: Session | null;
  pmUser: PMUser | null;
  pmUserLoaded: boolean;
  loading: boolean;
  activeProgramManagerId: string | null;
  programs: any[];
  signIn: (email: string, password: string) => Promise<{ error: Error | null; user?: User | null }>;
  signOut: () => Promise<void>;
  hasPermission: (perm: string) => boolean;
  hasAnyPermission: (...perms: string[]) => boolean;
  setAuthGuard: (active: boolean) => void;
}

const PMAuthContext = createContext<PMAuthContextType | undefined>(undefined);

export function PMAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [pmUser, setPMUser] = useState<PMUser | null>(null);
  const [pmUserLoaded, setPMUserLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeProgramManagerId, setActiveProgramManagerId] = useState<string | null>(null);
  const [programs, setPrograms] = useState<any[]>([]);
  const signingInRef = useRef(false);
  const signOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bootstrappedRef = useRef(false);

  const fetchPMUser = useCallback(async (userId: string) => {
    setPMUserLoaded(false);
    try {
      // Find active PM portal membership
      const { data: membership, error } = await (supabase as any)
        .from('pm_portal_users')
        .select('*, program_managers(id, status, white_label_name)')
        .eq('user_id', userId)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      if (error || !membership) {
        setPMUser(null);
        setActiveProgramManagerId(null);
        setPrograms([]);
        return;
      }

      const pm: PMUser = {
        id: membership.id,
        userId: membership.user_id,
        email: '',
        displayName: '',
        programManagerId: membership.program_manager_id,
        role: membership.role,
        permissions: membership.permissions || [],
        status: membership.status,
        mfaEnforced: membership.mfa_enforced,
        lastLoginAt: membership.last_login_at,
      };

      // Get email + display name from auth and client_profiles
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser?.email) pm.email = authUser.email;
      const meta = authUser?.user_metadata;
      let name = meta?.full_name || meta?.name || meta?.display_name || '';
      // Fallback: fetch from client_profiles if no name in auth metadata
      if (!name && authUser?.id) {
        const { data: profile } = await (supabase as any)
          .from('client_profiles')
          .select('full_name')
          .eq('user_id', authUser.id)
          .maybeSingle();
        if (profile?.full_name) name = profile.full_name;
      }
      pm.displayName = name;

      setPMUser(pm);
      setActiveProgramManagerId(membership.program_manager_id);

      // Fetch programs for this PM
      const { data: progs } = await (supabase as any)
        .from('card_programs')
        .select('id, name, code, status')
        .eq('program_manager_id', membership.program_manager_id);
      setPrograms(progs || []);

      // Update last_login_at
      await (supabase as any)
        .from('pm_portal_users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', membership.id);

      // Log session
      await (supabase as any)
        .from('pm_sessions')
        .insert({
          user_id: userId,
          program_manager_id: membership.program_manager_id,
          user_agent: navigator.userAgent,
          mfa_verified: true,
        });

    } catch (err) {
      console.error('[PMAuth] Error fetching PM user:', err);
      setPMUser(null);
    } finally {
      setPMUserLoaded(true);
    }
  }, []);

  const checkMfaComplete = async (): Promise<boolean> => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (data?.currentLevel === 'aal2') return true;
      if (data?.currentLevel === 'aal1' && data?.nextLevel === 'aal1') return true;
      return false;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!isMounted || event === 'INITIAL_SESSION') return;

      if (event === 'SIGNED_OUT') {
        if (signingInRef.current) return;
        if (signOutTimerRef.current) {
          clearTimeout(signOutTimerRef.current);
          signOutTimerRef.current = null;
        }
        signOutTimerRef.current = setTimeout(async () => {
          if (!isMounted || signingInRef.current) return;
          try {
            const { data: { session: s } } = await supabase.auth.getSession();
            if (!isMounted || signingInRef.current) return;
            if (!s?.user) {
              setSession(null);
              setUser(null);
              setPMUser(null);
              setPMUserLoaded(true);
            }
          } catch { /* ignore */ }
        }, 800);
        return;
      }

      if (signOutTimerRef.current) {
        clearTimeout(signOutTimerRef.current);
        signOutTimerRef.current = null;
      }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (nextSession?.user) {
        setTimeout(async () => {
          if (!isMounted) return;
          const mfaDone = await checkMfaComplete();
          if (mfaDone) void fetchPMUser(nextSession.user.id);
        }, 0);
      } else {
        setPMUser(null);
        setPMUserLoaded(true);
      }
    });

    const bootstrap = async () => {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) {
          let mfaDone = false;
          for (let attempt = 0; attempt < 3; attempt++) {
            mfaDone = await checkMfaComplete();
            if (mfaDone || !isMounted) break;
            await new Promise(r => setTimeout(r, 500));
          }
          if (mfaDone) await fetchPMUser(s.user.id);
          else { setPMUser(null); setPMUserLoaded(true); }
        } else {
          setPMUser(null);
          setPMUserLoaded(true);
        }
        bootstrappedRef.current = true;
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void bootstrap();
    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (signOutTimerRef.current) clearTimeout(signOutTimerRef.current);
    };
  }, [fetchPMUser]);

  const setAuthGuard = useCallback((active: boolean) => {
    signingInRef.current = active;
  }, []);

  const signIn = async (email: string, password: string) => {
    signingInRef.current = true;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    signingInRef.current = false;
    if (error) return { error: error as Error };
    if (data?.session) {
      setSession(data.session);
      setUser(data.user);
      void fetchPMUser(data.user.id);
    }
    return { error: null, user: data?.user ?? null };
  };

  const signOut = useCallback(async () => {
    if (signingInRef.current) return;
    await supabase.auth.signOut();
    setPMUser(null);
    setActiveProgramManagerId(null);
    setPrograms([]);
  }, []);

  const hasPermission = useCallback((perm: string): boolean => {
    if (!pmUser) return false;
    if (pmUser.role === 'owner' || pmUser.role === 'admin') return true;
    return pmUser.permissions.includes(perm);
  }, [pmUser]);

  const hasAnyPermission = useCallback((...perms: string[]): boolean => {
    if (!pmUser) return false;
    if (pmUser.role === 'owner' || pmUser.role === 'admin') return true;
    return perms.some(p => pmUser.permissions.includes(p));
  }, [pmUser]);

  return (
    <PMAuthContext.Provider value={{
      user, session, pmUser, pmUserLoaded, loading,
      activeProgramManagerId, programs,
      signIn, signOut, hasPermission, hasAnyPermission, setAuthGuard,
    }}>
      {children}
    </PMAuthContext.Provider>
  );
}

export function usePMAuth() {
  const ctx = useContext(PMAuthContext);
  if (!ctx) throw new Error('usePMAuth must be used within PMAuthProvider');
  return ctx;
}
